import { getUserById } from '@brands/services/identity/getUserById';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from './useReduxHook';

type DivisionScopeActor = Pick<UserProfile, 'role' | 'divisions'>;

/**
 * The signed-in user as the division-scoping helpers need them. A Client Admin's own divisions
 * are left off `/me`, which is what the auth state holds, so they're read from the admin user
 * record instead. Until that lands this returns null, letting callers hold the division pickers
 * back rather than briefly offer divisions the admin can't assign.
 */
export const useDivisionScopeActor = (): DivisionScopeActor | null => {
  const { userInfo } = useAppSelector(selectAuth);
  const isClientAdmin = userInfo?.role?.name === UserRoleName.OrganizationAdmin;
  const [ownDivisions, setOwnDivisions] = useState<UserProfile['divisions'] | null>(null);

  useEffect(() => {
    if (!isClientAdmin || !userInfo?.id) return undefined;

    let cancelled = false;
    setOwnDivisions(null);
    getUserById(Number(userInfo.id))
      .then((me) => {
        if (!cancelled) setOwnDivisions(me.divisions || []);
      })
      .catch(() => {
        // Falls back to whole-tag; the server still refuses a division outside the admin's own.
        if (!cancelled) setOwnDivisions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isClientAdmin, userInfo?.id]);

  return useMemo(() => {
    if (!isClientAdmin) return userInfo;
    return ownDivisions ? { role: userInfo.role, divisions: ownDivisions } : null;
  }, [isClientAdmin, userInfo, ownDivisions]);
};
